// File import (POST /systems/import). The server reads the rows with AI in a background job; the
// request waits up to 20 s and then answers 202 {status: "running", job_id}. This provider then
// polls GET /systems/import/{job_id} every 2 s until "ready" (the new entries), "error" or
// "cancelled". It lives in the app shell, so the import goes on when the person closes the modal
// or opens another page, and it resumes after a reload (job id in localStorage). The import modal
// shows the result while it is open; otherwise a toast says how many entries were added.
//
// Timing (N05): the background worker runs at most 15 minutes (it stops starting batches after 11
// and the server marks a job without a heartbeat for 10 minutes as an error), so the client waits
// 15 minutes from the upload. Every status request has its own 15 s timeout, so a hung request
// cannot stop the deadline. At the deadline the client does NOT give up: it keeps the job id and
// shows "Uvoz traja dlje kot običajno." with "Preveri stanje" (one check now, then a fresh polling
// window) and "Prekliči" (POST /systems/import/{job_id}/cancel; entries added before stay).
// A new upload of the same file is offered only after the server confirmed a terminal failure, the
// upload itself was refused, or the import was cancelled; never after a client timeout or a
// network blip (the server would still be working on the first one).
// A result with skipped_rows > 0 says how many rows were read ("Obdelanih 300 od 420 vrstic.").

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { api, ApiError } from "../api";
import type { AISystem, ImportCounts, ImportJob } from "../types";
import { t } from "../i18n/sl";
import { errorMessage, useToast } from "../components/ui";
import { useSession } from "./session";

/** "stalled": the client deadline passed while the server still said "running" (the job id is kept). */
export type ImportStatus = "none" | "running" | "stalled" | "ready" | "error" | "cancelled";

export interface ImportInfo {
  status: ImportStatus;
  fileName?: string;
  /** epoch ms when the upload started (progress estimate) */
  startedAt?: number;
  /** error text ("error"), the cancel note ("cancelled"), or a failed check or cancel ("stalled") */
  error?: string;
  /** entries of the last finished import (also the ones kept by a cancelled import) */
  systems?: AISystem[];
  /** row counts of the last finished import (skipped_rows > 0: part of the file was not read) */
  counts?: ImportCounts;
  /** increases each time an import adds entries, so the register merges them */
  version: number;
  /** a new upload is safe: the server confirmed the failure or the cancel, or refused the upload */
  canRetry: boolean;
  /** "Preveri stanje" or "Prekliči" is under way */
  busy?: "check" | "cancel";
}

interface Saved {
  jobId: string;
  fileName: string;
  startedAt: number;
}

const POLL_MS = 2000;
/** Client deadline from the upload (and each fresh window after "Preveri stanje"): the worker's 15 minutes. */
const DEADLINE_MS = 15 * 60_000;
/** Each status request gives up after this; the next tick tries again. */
const STATUS_TIMEOUT_MS = 15_000;
const REGISTER = "/app/register";

const storeKey = (orgId: string) => `semafor_import_v1:${orgId}`;

/** The saved job of this company; checked with the server after a reload, never dropped by age. */
function loadSaved(orgId: string): Saved | null {
  try {
    const s = JSON.parse(localStorage.getItem(storeKey(orgId)) ?? "null") as Saved | null;
    return s && s.jobId ? s : null;
  } catch {
    return null;
  }
}

/** One status request with its own timeout (an aborted request counts as a network blip). */
async function statusWithTimeout(jobId: string): Promise<ImportJob> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), STATUS_TIMEOUT_MS);
  try {
    return await api.importStatus(jobId, ctrl.signal);
  } finally {
    clearTimeout(timer);
  }
}

/** The job is gone, or belongs to another company or session: forget it quietly. */
const isGone = (e: unknown) => e instanceof ApiError && [401, 403, 404].includes(e.status);

interface ImportCtx extends ImportInfo {
  start: (file: File) => Promise<void>;
  /** Uploads the last file again (when this tab still has it), else shows the file picker. */
  retry: () => void;
  /** "Preveri stanje": one status check now; still running -> polling again with a fresh window. */
  checkNow: () => Promise<void>;
  /** "Prekliči": stops the import on the server; entries added before stay. */
  cancel: () => Promise<void>;
  /** The modal shows the result while it watches; returns the unsubscribe function. */
  watch: () => () => void;
  /** Forget a shown result or error (the modal was closed). */
  clear: () => void;
}

const Ctx = createContext<ImportCtx | null>(null);

export function ImportJobProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const orgId = me?.org?.id ?? "none";
  const toast = useToast();
  const navigate = useNavigate();
  const loc = useLocation();
  const locRef = useRef(loc.pathname);
  locRef.current = loc.pathname;
  const watchers = useRef(0);
  /** the file of the last upload in this tab (for "Poskusi znova") */
  const lastFile = useRef<File | null>(null);

  const [saved] = useState(() => loadSaved(orgId));
  const [jobId, setJobId] = useState<string | null>(saved?.jobId ?? null);
  /** a new value restarts the polling (after "Preveri stanje") */
  const [pollKey, setPollKey] = useState(0);
  /** end of the current polling window; a restored job gets at least one check */
  const deadlineRef = useRef(saved ? Math.max(saved.startedAt + DEADLINE_MS, Date.now() + 1) : 0);
  const [info, setInfo] = useState<ImportInfo>(() =>
    saved
      ? { status: "running", fileName: saved.fileName, startedAt: saved.startedAt, version: 0, canRetry: false }
      : { status: "none", version: 0, canRetry: false },
  );

  const persist = useCallback(
    (s: Saved | null) => {
      try {
        if (s) localStorage.setItem(storeKey(orgId), JSON.stringify(s));
        else localStorage.removeItem(storeKey(orgId));
      } catch {
        /* storage blocked: the import still finishes on the server */
      }
    },
    [orgId],
  );

  const finish = useCallback(
    (status: "ready" | "error" | "cancelled", systems?: AISystem[], error?: string, counts?: ImportCounts) => {
      persist(null);
      setJobId(null);
      const watched = watchers.current > 0;
      const added = systems?.length ?? 0;
      setInfo((s) => ({
        status: watched ? status : "none",
        fileName: s.fileName,
        startedAt: s.startedAt,
        error,
        systems: status === "error" ? s.systems : (systems ?? []),
        counts: status === "error" ? s.counts : counts,
        version: s.version + (status === "ready" || added > 0 ? 1 : 0),
        canRetry: status !== "ready",
      }));
      if (watched) return;
      if (status === "error") {
        toast(error || t.register.importFailed, "error");
        return;
      }
      if (status === "cancelled") {
        toast(t.register.importCancelledToast(added), "info");
        return;
      }
      const onRegister = locRef.current.startsWith(REGISTER);
      const partial = isPartial(counts);
      toast(
        partial ? t.register.importDonePartial(added, counts!.processed_rows ?? 0, counts!.total_rows ?? 0) : t.register.importDone(added),
        "ok",
        onRegister && !partial ? undefined : { action: { label: t.common.open, onClick: () => navigate(REGISTER) } },
      );
    },
    [navigate, persist, toast],
  );
  const finishRef = useRef(finish);
  finishRef.current = finish;

  /** A terminal answer from the server finishes the import; true when it was terminal. */
  const settle = useCallback((r: ImportJob): boolean => {
    if (r.status === "ready") finishRef.current("ready", r.systems ?? [], undefined, countsOf(r));
    else if (r.status === "error") finishRef.current("error", undefined, r.error || t.register.importFailed);
    else if (r.status === "cancelled") finishRef.current("cancelled", r.systems ?? [], r.error || t.register.importCancelled, countsOf(r));
    else return false;
    return true;
  }, []);

  /** The job is unknown on the server (or belongs to someone else): forget it, nothing to show. */
  const forget = useCallback(() => {
    persist(null);
    setJobId(null);
    setInfo((s) => ({ ...s, status: "none", busy: undefined }));
  }, [persist]);

  /** The client deadline passed: keep the job and ask the person (check or cancel). */
  const stall = useCallback(() => {
    setInfo((s) => ({ ...s, status: "stalled", busy: undefined, error: undefined }));
    if (watchers.current === 0) {
      toast(t.register.importStalledToast, "info", { action: { label: t.common.open, onClick: () => navigate(REGISTER) } });
    }
  }, [navigate, toast]);

  // the polling effect runs only when the job or the window changes, never because a callback
  // got a new identity (that would restart polling after a stall)
  const settleRef = useRef(settle);
  settleRef.current = settle;
  const stallRef = useRef(stall);
  stallRef.current = stall;
  const forgetRef = useRef(forget);
  forgetRef.current = forget;

  // poll every 2 s while the server job runs, until the end of the polling window
  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    let busy = false; // one request at a time (each one gives up after 15 s)
    const overDeadline = () => Date.now() > deadlineRef.current;
    const tick = async () => {
      if (busy || !alive) return;
      busy = true;
      try {
        const r = await statusWithTimeout(jobId);
        if (!alive) return;
        if (settleRef.current(r)) {
          alive = false;
        } else if (overDeadline()) {
          alive = false;
          stallRef.current();
        }
      } catch (e) {
        if (!alive) return;
        if (isGone(e)) {
          alive = false;
          forgetRef.current();
        } else if (overDeadline()) {
          // a network blip or a hung request at the end of the window: still not a failure
          alive = false;
          stallRef.current();
        }
        // anything else is a network blip: try again on the next tick
      } finally {
        busy = false;
      }
    };
    void tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [jobId, pollKey]);

  const start = useCallback(
    async (file: File) => {
      const startedAt = Date.now();
      lastFile.current = file;
      setInfo((s) => ({ ...s, status: "running", fileName: file.name, startedAt, error: undefined, counts: undefined, canRetry: false, busy: undefined }));
      try {
        const r = await api.importSystems(file);
        if (Array.isArray(r)) {
          finishRef.current("ready", r);
        } else if ("job_id" in r && r.job_id) {
          deadlineRef.current = startedAt + DEADLINE_MS;
          persist({ jobId: r.job_id, fileName: file.name, startedAt });
          setJobId(r.job_id);
          setPollKey((k) => k + 1);
        } else if ("systems" in r) {
          finishRef.current("ready", r.systems ?? [], undefined, countsOf(r));
        } else {
          finishRef.current("error", undefined, t.register.importFailed);
        }
      } catch (e) {
        // the upload itself failed: safe to send again (the server answers the same file within
        // 10 minutes with the job it already has)
        finishRef.current("error", undefined, errorMessage(e));
      }
    },
    [persist],
  );

  const retry = useCallback(() => {
    if (info.canRetry && lastFile.current) void start(lastFile.current);
    else setInfo((s) => (s.status === "running" || s.status === "stalled" ? s : { ...s, status: "none", error: undefined }));
  }, [info.canRetry, start]);

  const checkNow = useCallback(async () => {
    if (!jobId) return;
    setInfo((s) => ({ ...s, busy: "check", error: undefined }));
    try {
      const r = await statusWithTimeout(jobId);
      if (settle(r)) return;
      // still running on the server: wait again, with a fresh window
      deadlineRef.current = Date.now() + DEADLINE_MS;
      setInfo((s) => ({ ...s, status: "running", busy: undefined }));
      setPollKey((k) => k + 1);
    } catch (e) {
      if (isGone(e)) forget();
      else setInfo((s) => ({ ...s, busy: undefined, error: t.register.importCheckFailed }));
    }
  }, [jobId, settle, forget]);

  const cancel = useCallback(async () => {
    if (!jobId) return;
    setInfo((s) => ({ ...s, busy: "cancel", error: undefined }));
    try {
      const r = await api.importCancel(jobId);
      if (!settle(r)) {
        // the server did not confirm the cancel: keep the job and let the person try again
        setInfo((s) => ({ ...s, busy: undefined, error: t.register.importCancelFailed }));
      }
    } catch (e) {
      if (isGone(e)) forget();
      else setInfo((s) => ({ ...s, busy: undefined, error: t.register.importCancelFailed }));
    }
  }, [jobId, settle, forget]);

  const watch = useCallback(() => {
    watchers.current += 1;
    return () => {
      watchers.current = Math.max(0, watchers.current - 1);
    };
  }, []);

  const clear = useCallback(
    () => setInfo((s) => (s.status === "running" || s.status === "stalled" ? s : { ...s, status: "none", error: undefined })),
    [],
  );

  const value = useMemo(() => ({ ...info, start, retry, checkNow, cancel, watch, clear }), [info, start, retry, checkNow, cancel, watch, clear]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function countsOf(r: ImportCounts): ImportCounts | undefined {
  if (r.total_rows == null && r.processed_rows == null && r.skipped_rows == null) return undefined;
  return { total_rows: r.total_rows, processed_rows: r.processed_rows, skipped_rows: r.skipped_rows };
}

/** True when the server read only part of the file. */
export function isPartial(c: ImportCounts | undefined): boolean {
  return !!c && (c.skipped_rows ?? 0) > 0 && c.processed_rows != null && c.total_rows != null;
}

export function useImportJob(): ImportCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useImportJob outside ImportJobProvider");
  return c;
}
